import { useEffect, useRef, useState } from 'react';
import { getLessonResults, LessonResults } from '../../../api/results';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import { useResultsDataContext } from '../../../context/ResultsDataContext';
import { useFilters } from '../../../context/filter-context/FilterContext';
import { CurrentOrPreviousGroup } from '../../../../../../api/groups';
import { Module } from '../../../../../../api/module';
import { Result } from '../../../../../../utils/results';

function mapLessonResults(lessonResults: LessonResults['scores']): Result[] {
  const scores: Result[] = [];

  Object.entries(lessonResults).forEach(([nodeId, members]) => {
    Object.entries(members).forEach(([memberId, scoresOfStudent]) => {
      scoresOfStudent.forEach(score => {
        scores.push({
          ...score,
          memberId,
          nodeId,
        });
      });
    });
  });

  return scores;
}

const apiRequestQueryString = new URLSearchParams({
  includeFreeExercises: 'true',
  includeCompletedAssignments: 'true',
  includeUncompletedAssignments: 'true',
});

export default function useResults() {
  const { selectedModule, selectedGroups } = useFilters();
  const { groups } = useResultsDataContext();
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const handleError = useThrowAsyncError();

  const prevSelectedGroups = useRef<CurrentOrPreviousGroup[]>([]);
  const prevSelectedModule = useRef<Module | undefined>(undefined);

  useEffect(() => {
    const isDifferentModule = selectedModule?.id !== prevSelectedModule.current?.id;
    const isDifferentGroups =
      selectedGroups.length !== prevSelectedGroups.current.length ||
      selectedGroups.some(g => !prevSelectedGroups.current.some(pg => pg.id === g.id));

    if (
      !selectedModule ||
      !selectedGroups.length ||
      !groups.some(g => g.id === selectedGroups[0].id) ||
      (!isDifferentModule && !isDifferentGroups)
    )
      return;

    prevSelectedGroups.current = selectedGroups;
    prevSelectedModule.current = selectedModule;

    setLoading(true);
    getLessonResults(selectedModule.id, selectedGroups[0].id, apiRequestQueryString)
      .then(resultsResponse => {
        setResults(mapLessonResults(resultsResponse.scores));
      })
      .catch(handleError)
      .finally(() => {
        setLoading(false);
      });
  }, [groups, handleError, selectedGroups, selectedModule]);

  return {
    results,
    loading,
  };
}
